// shell/components/row-state/row-state-manager.ts

import { IDisposable, RowState } from '../types';
import { AnimationTimeoutManager } from '../renderer-utils';

/**
 * Configuration for RowStateManager.
 */
export interface RowStateManagerConfig {
    /** How long "fresh" state lasts before auto-clearing (default: 10000ms) */
    freshDuration?: number;

    /** Delay before triggering removal callback for deleted rows (default: 1000ms) */
    deletedRemoveDelay?: number;

    /** Called when any row's state changes */
    onStateChange?: (key: string, state: RowState) => void;

    /** Called when a deleted row's removal delay expires */
    onDeletedExpired?: (key: string) => void;
}

const DEFAULT_FRESH_DURATION = 10000;
const DEFAULT_DELETED_REMOVE_DELAY = 1000;

/**
 * Manages visual states for list items (fresh, deleted, visited, unread).
 *
 * Integrates with renderers via the getRowState callback pattern:
 * ```typescript
 * const stateManager = new RowStateManager({ ... });
 *
 * const renderer = new TableRenderer({
 *     getRowState: (item) => stateManager.getState(getRowId(item)),
 *     // ...
 * });
 * ```
 *
 * Key features:
 * - Auto-clears `fresh` state after configured duration
 * - Fires `onDeletedExpired` callback after delete animation delay
 * - Uses AnimationTimeoutManager for proper cleanup
 * - Supports multiple simultaneous state flags per item
 */
export class RowStateManager implements IDisposable {
    private readonly states = new Map<string, RowState>();
    private readonly timeoutManager = new AnimationTimeoutManager();
    private readonly freshDuration: number;
    private readonly deletedRemoveDelay: number;
    private readonly onStateChange?: (key: string, state: RowState) => void;
    private readonly onDeletedExpired?: (key: string) => void;

    constructor(config: RowStateManagerConfig = {}) {
        this.freshDuration = config.freshDuration ?? DEFAULT_FRESH_DURATION;
        this.deletedRemoveDelay = config.deletedRemoveDelay ?? DEFAULT_DELETED_REMOVE_DELAY;
        this.onStateChange = config.onStateChange;
        this.onDeletedExpired = config.onDeletedExpired;
    }

    /**
     * Mark an item as fresh (newly added).
     * The fresh state will auto-clear after freshDuration.
     */
    markFresh(key: string): void {
        this.updateState(key, { fresh: true });

        // Schedule auto-clear of fresh state
        this.timeoutManager.schedule(`fresh-${key}`, () => {
            const state = this.states.get(key);
            if (state?.fresh) {
                this.updateState(key, { fresh: false });
            }
        }, this.freshDuration);
    }

    /**
     * Mark an item as deleted.
     * After deletedRemoveDelay, onDeletedExpired callback will be fired.
     */
    markDeleted(key: string): void {
        this.updateState(key, { deleted: true });

        // Schedule deletion expired callback
        this.timeoutManager.schedule(`deleted-${key}`, () => {
            this.onDeletedExpired?.(key);
        }, this.deletedRemoveDelay);
    }

    /**
     * Mark an item as visited.
     */
    markVisited(key: string): void {
        this.updateState(key, { visited: true });
    }

    /**
     * Mark an item as having unread content.
     */
    markUnread(key: string): void {
        this.updateState(key, { unread: true });
    }

    /**
     * Clear the unread state for an item.
     */
    clearUnread(key: string): void {
        this.updateState(key, { unread: false });
    }

    /**
     * Clear all state for an item.
     * Also cancels any pending timeouts for this item.
     */
    clearState(key: string): void {
        this.timeoutManager.cancel(`fresh-${key}`);
        this.timeoutManager.cancel(`deleted-${key}`);

        const hadState = this.states.has(key);
        this.states.delete(key);

        if (hadState) {
            this.onStateChange?.(key, {});
        }
    }

    /**
     * Clear all states for all items.
     * Also cancels all pending timeouts.
     */
    clearAll(): void {
        const keys = Array.from(this.states.keys());
        this.timeoutManager.cancelAll();
        this.states.clear();

        // Notify for each cleared item
        for (const key of keys) {
            this.onStateChange?.(key, {});
        }
    }

    /**
     * Get the current state for an item.
     * Returns an empty object if no state is set.
     *
     * Use this as the implementation for renderer's getRowState callback:
     * ```typescript
     * getRowState: (item) => stateManager.getState(getRowId(item))
     * ```
     */
    getState(key: string): RowState {
        return this.states.get(key) ?? {};
    }

    /**
     * Check if an item has any state set.
     */
    hasState(key: string): boolean {
        return this.states.has(key);
    }

    /**
     * Get all keys that have state.
     */
    getKeysWithState(): string[] {
        return Array.from(this.states.keys());
    }

    /**
     * Get all keys that are marked as fresh.
     */
    getFreshKeys(): string[] {
        return Array.from(this.states.entries())
            .filter(([, state]) => state.fresh)
            .map(([key]) => key);
    }

    /**
     * Get all keys that are marked as deleted.
     */
    getDeletedKeys(): string[] {
        return Array.from(this.states.entries())
            .filter(([, state]) => state.deleted)
            .map(([key]) => key);
    }

    /**
     * Get all keys that are marked as unread.
     */
    getUnreadKeys(): string[] {
        return Array.from(this.states.entries())
            .filter(([, state]) => state.unread)
            .map(([key]) => key);
    }

    /**
     * Get the count of unread items.
     */
    getUnreadCount(): number {
        let count = 0;
        for (const state of this.states.values()) {
            if (state.unread) count++;
        }
        return count;
    }

    /**
     * Update state for an item, merging with existing state.
     * Removes the item's entry if all state flags become false/undefined.
     */
    /**
     * Add a custom CSS class to an item.
     * The class will be applied by the renderer on the row/card element.
     */
    addClass(key: string, className: string): void {
        const existing = this.states.get(key) ?? {};
        const classes = new Set(existing.classes);
        classes.add(className);
        this.updateState(key, { classes });
    }

    /**
     * Remove a custom CSS class from an item.
     */
    removeClass(key: string, className: string): void {
        const existing = this.states.get(key);
        if (!existing?.classes?.has(className)) return;
        const classes = new Set(existing.classes);
        classes.delete(className);
        this.updateState(key, { classes });
    }

    private updateState(key: string, changes: Partial<RowState>): void {
        const existing = this.states.get(key) ?? {};
        const merged: RowState = {
            ...existing,
            ...changes
        };

        // Clean up false values
        const cleanState: RowState = {};
        if (merged.fresh) cleanState.fresh = true;
        if (merged.deleted) cleanState.deleted = true;
        if (merged.visited) cleanState.visited = true;
        if (merged.unread) cleanState.unread = true;
        if (merged.classes && merged.classes.size > 0) cleanState.classes = merged.classes;

        // If all flags are false and no custom classes, remove the entry
        const hasAnyState = Object.keys(cleanState).length > 0;

        if (hasAnyState) {
            this.states.set(key, cleanState);
        } else {
            this.states.delete(key);
        }

        this.onStateChange?.(key, cleanState);
    }

    dispose(): void {
        this.timeoutManager.dispose();
        this.states.clear();
    }
}
